// ProjectForm (fixed with coordinate glitch prevention)
import React, { useState, useRef, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { useToast } from "../../components/AdminSharedComponents";
import api from "../../../../../utils/api";
import ProjectFormComponent from "./ProjectFormComponents";

// Fix default Leaflet icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png",
  iconUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png",
  shadowUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png",
});

const ProjectForm = ({ isEdit = false }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();

  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markerRef = useRef(null);
  const userChangedCoordsRef = useRef(false);

  const [formData, setFormData] = useState({
    name: "",
    client_name: "",
    location: "",
    status: "In Progress",
    start_date: "",
    end_date: "",
    pic: "",
    coordinates: null,
  });
  const [manualCoordinates, setManualCoordinates] = useState({
    lat: "",
    lng: "",
  });
  const [loading, setLoading] = useState(isEdit);
  const [submitting, setSubmitting] = useState(false);
  const [locationSearching, setLocationSearching] = useState(false);

  // Tambahkan ref untuk mencegah overwrite setelah pertama kali load
  const hasLoadedProject = useRef(false);

  // fetch existing project for edit
  useEffect(() => {
    if (!isEdit || !id || hasLoadedProject.current) return;

    const fetchProject = async () => {
      try {
        const res = await api.get(`/api/projects/${id}`);
        const project = res.data;

        let coords = null;
        if (project.coordinates?.coordinates) {
          coords = {
            lat: project.coordinates.coordinates[1],
            lng: project.coordinates.coordinates[0],
          };
        }

        setFormData({
          name: project.name || "",
          client_name: project.client_name || "",
          location: project.location || "",
          status: project.status || "In Progress",
          start_date: project.start_date
            ? project.start_date.split("T")[0]
            : "",
          end_date: project.end_date ? project.end_date.split("T")[0] : "",
          pic: project.pic || "",
          coordinates: coords,
        });

        if (coords) {
          setManualCoordinates({
            lat: coords.lat.toString(),
            lng: coords.lng.toString(),
          });
        }

        hasLoadedProject.current = true;
        setLoading(false);
      } catch (err) {
        console.error("Error fetching project:", err);
        toast.error("Failed to load project");
        navigate("/admin/projects");
      }
    };

    fetchProject();
  }, [isEdit, id, navigate, toast]);

  // Initialize map
  useEffect(() => {
    if (!mapRef.current || mapInstanceRef.current) return;

    const lat = formData.coordinates?.lat ?? -7.2504;
    const lng = formData.coordinates?.lng ?? 112.7688;
    const zoom = formData.coordinates ? 15 : 13;

    mapInstanceRef.current = L.map(mapRef.current).setView([lat, lng], zoom);

    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution:
        '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
    }).addTo(mapInstanceRef.current);

    // click handler
    mapInstanceRef.current.on("click", (e) => {
      const { lat, lng } = e.latlng;
      if (markerRef.current)
        mapInstanceRef.current.removeLayer(markerRef.current);
      markerRef.current = L.marker([lat, lng]).addTo(mapInstanceRef.current);

      userChangedCoordsRef.current = true;
      setFormData((prev) => ({ ...prev, coordinates: { lat, lng } }));
      setManualCoordinates({ lat: lat.toFixed(6), lng: lng.toFixed(6) });
      reverseGeocode(lat, lng);
    });

    if (formData.coordinates) {
      const { lat: cLat, lng: cLng } = formData.coordinates;
      markerRef.current = L.marker([cLat, cLng]).addTo(mapInstanceRef.current);
      mapInstanceRef.current.setView([cLat, cLng], 15);
    }

    setTimeout(() => {
      mapInstanceRef.current?.invalidateSize();
    }, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // React to changes in coordinates
  useEffect(() => {
    if (!mapInstanceRef.current) return;

    if (formData.coordinates) {
      const { lat, lng } = formData.coordinates;

      if (markerRef.current) {
        markerRef.current.setLatLng([lat, lng]);
      } else {
        markerRef.current = L.marker([lat, lng]).addTo(mapInstanceRef.current);
      }

      mapInstanceRef.current.setView([lat, lng], 15);
    } else if (markerRef.current) {
      markerRef.current.remove();
      markerRef.current = null;
    }
  }, [formData.coordinates]);

  // Cleanup map on unmount
  useEffect(() => {
    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, []);

  const reverseGeocode = async (lat, lng) => {
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&zoom=18&addressdetails=1`
      );
      const data = await response.json();
      if (data.display_name) {
        setFormData((prev) => ({
          ...prev,
          location: data.display_name,
        }));
      }
    } catch (error) {
      console.error("Error reverse geocoding:", error);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleManualCoordinateChange = (e) => {
    const { name, value } = e.target;
    setManualCoordinates((prev) => ({ ...prev, [name]: value }));
  };

  const applyManualCoordinates = () => {
    const lat = parseFloat(manualCoordinates.lat);
    const lng = parseFloat(manualCoordinates.lng);

    if (isNaN(lat) || isNaN(lng)) {
      toast.error("Please enter valid latitude and longitude values");
      return;
    }

    if (lat < -90 || lat > 90) {
      toast.error("Latitude must be between -90 and 90");
      return;
    }

    if (lng < -180 || lng > 180) {
      toast.error("Longitude must be between -180 and 180");
      return;
    }

    userChangedCoordsRef.current = true;
    setFormData((prev) => ({ ...prev, coordinates: { lat, lng } }));
    toast.success("Coordinates applied successfully!");
  };

  const handleLocationSearch = async () => {
    if (!formData.location.trim()) {
      toast.error("Please enter a location to search");
      return;
    }

    try {
      setLocationSearching(true);
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(
          formData.location
        )}&limit=1`
      );
      const data = await response.json();

      if (data.length > 0) {
        const { lat, lon } = data[0];
        const coords = { lat: parseFloat(lat), lng: parseFloat(lon) };

        userChangedCoordsRef.current = true;
        setFormData((prev) => ({ ...prev, coordinates: coords }));
        setManualCoordinates({
          lat: coords.lat.toFixed(6),
          lng: coords.lng.toFixed(6),
        });

        toast.success("Location found on map!");
      } else {
        toast.error("Location not found");
      }
    } catch (err) {
      console.error(err);
      toast.error("Error searching location");
    } finally {
      setLocationSearching(false);
    }
  };

  const clearCoordinates = () => {
    setFormData((p) => ({ ...p, coordinates: null, location: "" }));
    setManualCoordinates({ lat: "", lng: "" });
    if (markerRef.current && mapInstanceRef.current) {
      mapInstanceRef.current.removeLayer(markerRef.current);
      markerRef.current = null;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) return toast.error("Project name required");

    if (formData.start_date && formData.end_date) {
      if (new Date(formData.start_date) > new Date(formData.end_date)) {
        return toast.error("End date must be after start date");
      }
    }

    try {
      setSubmitting(true);
      const payload = {
        name: formData.name,
        client_name: formData.client_name || null,
        location: formData.location || null,
        status: formData.status,
        start_date: formData.start_date || null,
        end_date: formData.end_date || null,
        pic: formData.pic || null,
      };

      let projectId = id;
      if (isEdit) {
        await api.put(`/api/projects/${id}`, payload);
      } else {
        const res = await api.post("/api/projects", payload);
        projectId = res.data.id;
      }

      if (formData.coordinates && projectId) {
        await api.put(`/api/projects/${projectId}/location`, {
          latitude: formData.coordinates.lat,
          longitude: formData.coordinates.lng,
          location: formData.location,
        });
      } else if (isEdit) {
        try {
          await api.delete(`/api/projects/${id}/location`);
        } catch {
          console.log("No location to clear");
        }
      }

      toast.success(`Project ${isEdit ? "updated" : "created"} successfully!`);
      navigate(isEdit ? `/admin/projects/show/${id}` : "/admin/projects");
    } catch (err) {
      console.error(err);
      toast.error(`Failed to ${isEdit ? "update" : "create"} project`);
      setSubmitting(false);
    }
  };

  return (
    <ProjectFormComponent
      formId="project-form"
      isEdit={isEdit}
      loading={loading}
      submitting={submitting}
      title={isEdit ? "Edit Project" : "Add New Project"}
      backLink="/admin/projects"
      backText="Back to Projects"
      cancelLink={isEdit ? `/admin/projects/show/${id}` : undefined}
      cancelText={isEdit ? "Cancel" : undefined}
      submitText={isEdit ? "Update Project" : "Create Project"}
      loadingText={isEdit ? "Updating..." : "Creating..."}
      submitIcon={null}
      formData={formData}
      manualCoordinates={manualCoordinates}
      handleChange={handleChange}
      handleManualCoordinateChange={handleManualCoordinateChange}
      applyManualCoordinates={applyManualCoordinates}
      clearCoordinates={clearCoordinates}
      handleLocationSearch={handleLocationSearch}
      locationSearching={locationSearching}
      mapRef={mapRef}
      handleSubmit={handleSubmit}
    />
  );
};

export default ProjectForm;
